/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/** The plan cards as a swipeable track, for phones. */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, UnstyledButton } from '@/shared/ui';
import {
  arrowState, indexAfterDrag, isClick, nearestIndex,
} from './PlanChoice.carousel';

/** The chevron, pointing whichever way it is asked to. */
const Chevron: React.FC<{ back?: boolean }> = ({ back }) => (
  <svg
    width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    style={{ transform: back ? 'rotate(180deg)' : undefined }}
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);

export interface PlanCarouselProps {
  /** One child per card, in catalogue order. */
  children: React.ReactNode[];
}

/**
 * A horizontal track with snap, dots and arrows.
 *
 * Native scrolling does the work on a phone, which is what makes it feel
 * right: momentum, rubber-banding at the ends and the interrupted flick are
 * the platform's, not ours. The pointer handlers exist for the mouse, which
 * has none of that, and they hand control straight back to the scroller.
 *
 * @param props - The cards.
 * @returns The carousel.
 */
export const PlanCarousel: React.FC<PlanCarouselProps> = ({ children }) => {
  const { t } = useTranslation('@pages/onboarding');
  const track = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ startX: number; startScroll: number; from: number } | null>(null);
  const travelled = useRef(0);
  const count = React.Children.count(children);

  /** One card plus the gap between it and the next. */
  const step = useCallback((): number => {
    const first = track.current?.firstElementChild as HTMLElement | undefined;
    if (!first || !track.current) return 0;
    const second = first.nextElementSibling as HTMLElement | null;
    // Measured from the two cards rather than computed from the percentage:
    // the gap is a token and the width is a percentage of a viewport that
    // changes with the scrollbar.
    return second ? second.offsetLeft - first.offsetLeft : first.offsetWidth;
  }, []);

  const goTo = useCallback((next: number) => {
    const width = step();
    track.current?.scrollTo({ left: next * width, behavior: 'smooth' });
    setIndex(next);
  }, [step]);

  // The dot follows the finger, not only the arrows: on a phone most of the
  // movement never goes through this component at all.
  const onScroll = useCallback(() => {
    if (drag.current) return;
    const element = track.current;
    if (!element) return;
    setIndex(nearestIndex(element.scrollLeft, step(), count));
  }, [count, step]);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    // Touch is left to the browser: taking it over here would replace
    // momentum and rubber-banding with a worse copy of both.
    if (event.pointerType === 'touch' || !track.current) return;
    drag.current = {
      startX: event.clientX,
      startScroll: track.current.scrollLeft,
      from: index,
    };
    travelled.current = 0;
    setDragging(true);
    track.current.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const state = drag.current;
    if (!state || !track.current) return;
    travelled.current = event.clientX - state.startX;
    track.current.scrollLeft = state.startScroll - travelled.current;
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const state = drag.current;
    if (!state) return;
    drag.current = null;
    setDragging(false);
    track.current?.releasePointerCapture(event.pointerId);
    goTo(indexAfterDrag(state.from, travelled.current, step(), count));
  };

  // A click that follows a drag belongs to the drag. Without this the
  // checkout opens on whichever card the pointer happened to end over.
  const onClickCapture = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isClick(travelled.current)) return;
    event.preventDefault();
    event.stopPropagation();
    travelled.current = 0;
  };

  useEffect(() => {
    const element = track.current;
    if (!element) return undefined;
    element.addEventListener('scroll', onScroll, { passive: true });
    return () => element.removeEventListener('scroll', onScroll);
  }, [onScroll]);

  const arrows = arrowState(index, count);

  return (
    <div
      data-testid="plan-carousel"
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', width: '100%', maxWidth: '100%' }}
    >
      <div
        ref={track}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClickCapture={onClickCapture}
        className="no-scrollbar"
        style={{
          display: 'flex', gap: 'var(--space-3)',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          padding: 'var(--space-3) calc((100% - 90%) / 2) var(--space-1)',
          overflowX: 'auto', overflowY: 'hidden',
          scrollSnapType: dragging ? 'none' : 'x mandatory',
          cursor: dragging ? 'grabbing' : 'grab',
          touchAction: 'pan-x',
          minWidth: 0,
        }}
      >
        {React.Children.map(children, (card, at) => (
          <div
            key={at}
            data-testid={`plan-carousel-slide-${at}`}
            style={{
              flex: '0 0 90%', minWidth: 0, scrollSnapAlign: 'center',
              display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)',
            }}
          >
            {card}
          </div>
        ))}
      </div>

      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 'var(--space-3)',
        }}
      >
        <UnstyledButton
          onClick={() => goTo(index - 1)}
          disabled={!arrows.back}
          aria-label={t('planChoice.carousel.previous')}
          data-testid="plan-carousel-back"
          style={{
            display: 'flex', background: 'none',
            cursor: arrows.back ? 'pointer' : 'default',
            color: arrows.back ? 'var(--text-secondary)' : 'var(--border-medium)',
          }}
        >
          <Chevron back />
        </UnstyledButton>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1-5)' }}>
          {React.Children.map(children, (_, at) => (
            <UnstyledButton
              key={at}
              onClick={() => goTo(at)}
              aria-label={t('planChoice.carousel.goTo', { number: at + 1 })}
              aria-current={at === index ? 'true' : undefined}
              data-testid={`plan-carousel-dot-${at}`}
              style={{
                width: at === index ? 22 : 8, height: 8, padding: 0,
                borderRadius: 'var(--radius-full)', cursor: 'pointer',
                background: at === index ? 'var(--seed-base)' : 'var(--border-medium)',
                transition: 'width 160ms ease, background 160ms ease',
              }}
            >
              {/* The dot is the control; its name is on the button. */}
              <Text component="span" aria-hidden="true" />
            </UnstyledButton>
          ))}
        </div>

        <UnstyledButton
          onClick={() => goTo(index + 1)}
          disabled={!arrows.forward}
          aria-label={t('planChoice.carousel.next')}
          data-testid="plan-carousel-forward"
          style={{
            display: 'flex', background: 'none',
            cursor: arrows.forward ? 'pointer' : 'default',
            color: arrows.forward ? 'var(--text-secondary)' : 'var(--border-medium)',
          }}
        >
          <Chevron />
        </UnstyledButton>
      </div>
    </div>
  );
};

export default PlanCarousel;
